import { isUndefined, mapValues, omitBy } from 'lodash/fp';
import type { UIEvent } from 'react';
import { memo, useContext, useLayoutEffect } from 'react';

import type { Touchable } from '@skippay/core/@types/touchable';

import { LayoutContext } from 'providers/Layout';

interface Props {
	centered?: boolean;
	coloredBackground?: boolean;
	fullHeight?: boolean;
	fullHeightContent?: boolean;
	hasBanner?: boolean;
	hasScrollToTop?: boolean;
	scrollToTopOffset?: number;
	hasPublicContent?: boolean;
	headerBackLink?: boolean;
	headerBellLink?: boolean | Touchable['onClick'];
	headerInfoLink?: Touchable & { testId: string; analyticsId: string };
	headerLinkOnClick?: Touchable['onClick'];
	headerLinkTo?: Touchable['to'];
	headerLinkToBackInHistory?: boolean;
	headerTitle?: string | JSX.Element;
	headerType?: 'secondary' | 'transparent';
	loading?: boolean;
	mallCoinsAmount?: number;
	navWithoutBorder?: boolean;
	onScroll?: (event: UIEvent<HTMLDivElement>) => void;
	profileName?: string;
	rightContent?: JSX.Element;
	sideMenuBanner?: JSX.Element;
	scrollHeaderLinkTo?: Touchable['to'];
	scrollHeaderTitle?: string;
	simple?: boolean;
	subheader?: React.JSX.Element;
	unreadNotificationsCount?: number;
	withoutSideMenu?: boolean;
	withoutSystemOutageBanner?: boolean;
	registrationLink?: Touchable['to'];
}

const LayoutProperties = memo(function LayoutProperties(props: Props) {
	const {
		centered,
		coloredBackground,
		fullHeight,
		fullHeightContent,
		hasBanner,
		hasPublicContent,
		hasScrollToTop,
		headerBackLink,
		headerBellLink,
		headerInfoLink,
		headerLinkOnClick,
		headerLinkTo,
		headerLinkToBackInHistory,
		headerType,
		headerTitle,
		loading,
		mallCoinsAmount,
		navWithoutBorder,
		onScroll,
		profileName,
		rightContent,
		sideMenuBanner,
		scrollHeaderLinkTo,
		scrollHeaderTitle,
		subheader,
		simple,
		unreadNotificationsCount,
		withoutSideMenu,
		withoutSystemOutageBanner,
		registrationLink,
		scrollToTopOffset,
	} = props;
	const { setLayoutProps } = useContext(LayoutContext);

	useLayoutEffect(() => {
		const ownedProps = omitBy(isUndefined, {
			centered,
			coloredBackground,
			fullHeight,
			fullHeightContent,
			hasBanner,
			hasPublicContent,
			headerBackLink,
			headerBellLink,
			headerInfoLink,
			headerLinkTo,
			headerLinkOnClick,
			headerType,
			headerTitle,
			loading,
			navWithoutBorder,
			mallCoinsAmount,
			headerLinkToBackInHistory,
			onScroll,
			profileName,
			sideMenuBanner,
			simple,
			scrollHeaderLinkTo,
			scrollHeaderTitle,
			subheader,
			unreadNotificationsCount,
			withoutSideMenu,
			withoutSystemOutageBanner,
			hasScrollToTop,
			scrollToTopOffset,
			rightContent,
			registrationLink,
		});
		setLayoutProps(ownedProps);
		return () => setLayoutProps(mapValues(() => undefined, ownedProps));
	}, [
		centered,
		coloredBackground,
		fullHeight,
		fullHeightContent,
		hasBanner,
		hasPublicContent,
		headerBackLink,
		headerBellLink,
		headerLinkOnClick,
		headerLinkTo,
		headerTitle,
		headerType,
		loading,
		mallCoinsAmount,
		headerLinkToBackInHistory,
		onScroll,
		profileName,
		sideMenuBanner,
		simple,
		scrollHeaderLinkTo,
		scrollHeaderTitle,
		subheader,
		unreadNotificationsCount,
		withoutSideMenu,
		hasScrollToTop,
		scrollToTopOffset,
		rightContent,
		withoutSystemOutageBanner,
		registrationLink,
	]);

	return null;
});

export default LayoutProperties;
